<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const defaults = {
	name: 'Product Name',
	imageURL: '',
	description: '',
	note: '',
	priceMsg: '',
	btnTxt: 'Order Now',
	link: '',
	priceInfo: '',
	about: '',
	features: '',
	author: '',
	endorsements: [],
}
const schema = defineSchema({
	name: $.text('Title'),
	imageURL: $.upload('Hero Image'),
	description: $.richtext('Intro', { basic: true }),
	note: $.richtext('Intro Note', { basic: true }),
	priceMsg: $.richtext('Price / Lead', { basic: true }),
	btnTxt: $.text('Button Text'),
	link: $.text('Button Link'),
	priceInfo: $.richtext('Buy Note', { basic: true }),
	about: $.richtext('About Section', { basic: true }),
	features: $.richtext('Features Section', { basic: true }),
	author: $.richtext('Author Section', { basic: true }),
	endorsements: $.array('Endorsements', defineSchema({
		id: $.text('ID'),
		body: $.richtext('Quote', { basic: true }),
		name: $.text('Attribution'),
	}), { collapsible: true }),
})
export const BlockStoreProduct = defineBlock({ pickerHidden: true,
	thumbnail: '/block-thumbs/BlockStoreProduct.svg',
	category: 'Content',
	order: 350,
	name: 'BlockStoreProduct',
	label: 'Store Product',
	icon: 'storefront',
	schema,
	defaults,
})
</script>
<script setup lang="ts">
import { ref, computed } from 'vue'

import { Btn, Image, Swiper } from '@bagelink/vue'
import { useBoundPageItem } from '@/composables/useBoundPageItem'

/**
 * Store product page — a faithful recreation of thetorah.com/store/:slug.
 *
 * Renders the full product layout from a single bound `$page` record (falling
 * back to editor props): a centered gray hero, the intro rich-text, a bordered
 * "buy card" (note + CTA + info), the About / Features / Author rich-text
 * sections, and an endorsements carousel — mirroring the original Webflow
 * structure (`cb-top`, `cb-wrap`, `buy-card`, `rtf-cb`) and its CSS.
 *
 * One self-contained block keeps the page identical to the source and easy to
 * maintain, while the generic BlockProductHero / BlockCTA / BlockTestimonials
 * remain available for other, non-store pages.
 */

interface Endorsement { id?: number | string, body?: string, name?: string }




const props = defineProps<{
	name?: string
	imageURL?: string
	description?: string
	note?: string
	priceMsg?: string
	btnTxt?: string
	link?: string
	priceInfo?: string
	about?: string
	features?: string
	author?: string
	endorsements?: Endorsement[]
}>()

const bound = useBoundPageItem<Record<string, any>>()

/** Bound record field, falling back to the matching editor prop. */
function field(key: keyof typeof props): string {
	return String(bound.value?.[key as string] ?? props[key] ?? '')
}

const title = computed(() => bound.value?.name || bound.value?.title || props.name || '')
const heroImage = computed(() => bound.value?.image?.imageURL || bound.value?.image || props.imageURL || '')
const product = computed(() => ({
	description: field('description'),
	note: field('note'),
	priceMsg: field('priceMsg'),
	link: field('link'),
	priceInfo: field('priceInfo'),
	about: field('about'),
	features: field('features'),
	author: field('author'),
}))
const btnTxt = computed(() => bound.value?.btnTxt || props.btnTxt || 'Order Now')
const endorsements = computed<Endorsement[]>(() => bound.value?.endorsements ?? props.endorsements ?? [])

/** Intro text = description + note stacked (as in the source `cb-wrap`). */
const intro = computed(() => [product.value.description, product.value.note].filter(Boolean).join(''))

// Endorsements carousel state.
const currentSlide = ref(0)
function onSlideChange(index: number) { currentSlide.value = index }
function nextSlide() { currentSlide.value = currentSlide.value < endorsements.value.length - 1 ? currentSlide.value + 1 : 0 }
function prevSlide() { currentSlide.value = currentSlide.value > 0 ? currentSlide.value - 1 : endorsements.value.length - 1 }
</script>

<template>
	<div class="storeText pb-4 m_pb-2">
		<!-- Hero: centered title + banner image on a light gray band -->
		<div class="txt-center bg-gray-20 flex column flex-center py-3 m_py-2">
			<h1 class="regular txt40 m_txt32 m-0 line-height-14 px-1 pb-1">
				{{ title }}
			</h1>
			<Image v-if="heroImage" :src="heroImage" :alt="title" class="w-100p" />
		</div>

		<!-- Intro rich-text -->
		<div v-if="intro" class="w760px mx-auto px-075 pt-3 m_pt-1" v-html="intro" />

		<!-- Buy card: lead/price message + CTA button + info, top/bottom rules -->
		<div v-if="product.link || product.priceMsg" class="w760px mx-auto px-075">
			<div class="txt-center border-top border-bottom border-black my-3 py-2 m_my-1 m_py-1">
				<div v-if="product.priceMsg" class="pretty" v-html="product.priceMsg" />
				<Btn size="xl" :href="product.link" target="_blank" class="my-1 bg-red-130 radius-05">
					{{ btnTxt }}
				</Btn>
				<div v-if="product.priceInfo" v-html="product.priceInfo" />
			</div>
		</div>

		<!-- Content sections -->
		<div v-if="product.about" class="w760px mx-auto px-075 pt-3 m_pt-1" v-html="product.about" />
		<div v-if="product.features" class="w760px mx-auto px-075 pt-3 m_pt-1" v-html="product.features" />
		<div v-if="product.author" class="w760px mx-auto px-075 pt-3 m_pt-1" v-html="product.author" />

		<!-- Endorsements -->
		<div v-if="endorsements.length" class="w760px mx-auto px-075 pt-3 relative">
			<h3>Endorsements</h3>
			<div class="bg-gray-20 p-2 radius-1 m_p-1-5">
				<div v-if="endorsements.length === 1">
					<div class="txt17" v-html="endorsements[0].body" />
					<p class="bold txt17 pt-1">
						{{ endorsements[0].name }}
					</p>
				</div>
				<div v-else>
					<img src="../../assets/marks.svg" alt="marks" class="mb-1">
					<Swiper
						v-model:index="currentSlide"
						:items="endorsements"
						:slides-per-view="1"
						:navigation="false"
						loop
						@slide-change="onSlideChange"
					>
						<template #default="{ item }">
							<div>
								<div class="txt17 m_txt15" v-html="item.body" />
								<p class="bold txt17 m_txt15 pt-1">
									{{ item.name }}
								</p>
							</div>
						</template>
					</Swiper>
					<div class="absolute end-3 flex gap-1">
						<Btn class="radius-0 bg-gray-100" size="xl" icon="arrow_back" @click="prevSlide" />
						<Btn class="radius-0 bg-gray-100" size="xl" icon="arrow_forward" @click="nextSlide" />
					</div>
				</div>
			</div>
		</div>

		<!-- Repeat buy card at the bottom (as in the source) -->
		<div v-if="product.link" class="w760px mx-auto px-075">
			<div class="txt-center border-top border-bottom border-black my-3 py-2 m_my-1 m_py-1">
				<div v-if="product.priceMsg" class="pretty" v-html="product.priceMsg" />
				<Btn size="xl" :href="product.link" target="_blank" class="my-1 bg-red-130 radius-05">
					{{ btnTxt }}
				</Btn>
				<div v-if="product.priceInfo" v-html="product.priceInfo" />
			</div>
		</div>
	</div>
</template>
